import type { Photo } from "@/core/photo"
import type { Mask } from "@/core/types"
import type { AIMattingDetection } from "@/lib/ai/detection/ai-detection"
import type { MaskCache } from "@/storage/mask-cache"
import {
	AIMaskNotDetectedError,
	canonicalizeAlphaMask,
	decodeAlphaMask,
	getArtifactDescriptor,
	type MaskResult,
	type RenderMaskAnalysisInput,
	writeArtifact,
} from "./ai-mask-result"

type StandardMaskDetection = Pick<AIMattingDetection, "getSubjectMask" | "getSkyMask">

export class AIStandardMaskProvider {
	private detectionInput: ImageBitmap | null = null
	private inFlightCanonicalAlphaMasks = new Map<string, Promise<MaskResult>>()

	constructor(
		private readonly proxy: StandardMaskDetection,
		private readonly renderAnalysisInput: RenderMaskAnalysisInput,
		private readonly cache?: MaskCache,
	) {}

	async getMaskResult(photo: Photo, mask: Mask, signal: AbortSignal) {
		switch (mask.type) {
			case "subject":
			case "background":
				return await this.getSubjectMaskResult(photo, signal)
			case "sky":
				return await this.getSkyMaskResult(photo, signal)
			default:
				throw new Error("No detection for mask type")
		}
	}

	cleanup() {
		this.inFlightCanonicalAlphaMasks.clear()
		this.detectionInput?.close()
		this.detectionInput = null
	}

	private async getSubjectMaskResult(photo: Photo, signal: AbortSignal) {
		const adjustedGamma = 0.65
		const descriptor = getArtifactDescriptor(
			this.cache,
			photo,
			"subject",
			"subject-v1-gamma-0.65",
		)
		const result = await this.getCanonicalAlphaMaskResult(
			descriptor?.id ?? `${photo.id}:subject-v1-gamma-0.65`,
			async () => {
				if (descriptor) {
					const cached = await this.cache?.readArtifactBundle(descriptor.id, ["alpha"])
					const chunk = cached?.chunks[0]
					if (chunk) {
						return decodeAlphaMask(chunk)
					}
				}
				const input = await this.getDetectionInput(photo)
				if (!input) {
					throw new Error("Could not create subject detection input")
				}
				const inferred = await this.proxy.getSubjectMask(input, adjustedGamma)
				if (!inferred) {
					throw new AIMaskNotDetectedError("Could not detect subject")
				}
				const canonical = canonicalizeAlphaMask(descriptor?.id, "alpha", inferred)
				await writeArtifact(
					this.cache,
					descriptor,
					{ width: canonical.result.width, height: canonical.result.height },
					[canonical.chunk],
				)
				return canonical.result
			},
		)
		signal.throwIfAborted()
		return result
	}

	private async getSkyMaskResult(photo: Photo, signal: AbortSignal) {
		const descriptor = getArtifactDescriptor(this.cache, photo, "sky", "sky-v1")
		const result = await this.getCanonicalAlphaMaskResult(
			descriptor?.id ?? `${photo.id}:sky-v1`,
			async () => {
				if (descriptor) {
					const cached = await this.cache?.readArtifactBundle(descriptor.id, ["alpha"])
					const chunk = cached?.chunks[0]
					if (chunk) {
						return decodeAlphaMask(chunk)
					}
				}
				const input = await this.getDetectionInput(photo)
				if (!input) {
					throw new Error("Could not create sky detection input")
				}
				const inferred = await this.proxy.getSkyMask(input)
				if (!inferred) {
					throw new AIMaskNotDetectedError("Could not detect sky")
				}
				const canonical = canonicalizeAlphaMask(descriptor?.id, "alpha", inferred)
				await writeArtifact(
					this.cache,
					descriptor,
					{ width: canonical.result.width, height: canonical.result.height },
					[canonical.chunk],
				)
				return canonical.result
			},
		)
		signal.throwIfAborted()
		return result
	}

	private async getCanonicalAlphaMaskResult(id: string, compute: () => Promise<MaskResult>) {
		const cached = this.inFlightCanonicalAlphaMasks.get(id)
		if (cached) {
			return await cached
		}
		const promise = compute().finally(() => {
			if (this.inFlightCanonicalAlphaMasks.get(id) === promise) {
				this.inFlightCanonicalAlphaMasks.delete(id)
			}
		})
		this.inFlightCanonicalAlphaMasks.set(id, promise)
		return await promise
	}

	private async getDetectionInput(photo: Photo) {
		if (this.detectionInput) {
			return this.detectionInput
		}

		const input = await this.renderAnalysisInput(photo, [1024, 1024])
		this.detectionInput = input ?? null
		return this.detectionInput
	}
}
